import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DAYS = ["L", "M", "M", "J", "V", "S", "D"];
const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const keyOf = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export default function ScheduleCalendar({ posts }) {
  const [cursor, setCursor] = useState(() => new Date());
  const [selDay, setSelDay] = useState(null);

  const byDay = useMemo(() => {
    const map = {};
    posts.filter((p) => p.status === "scheduled" && p.scheduled_at).forEach((p) => {
      const d = new Date(p.scheduled_at);
      (map[keyOf(d)] ||= []).push(p);
    });
    return map;
  }, [posts]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Lunes = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const selPosts = selDay ? (byDay[selDay] || []) : [];

  return (
    <div data-testid="social-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="text-navy/40 hover:text-violet" data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></button>
        <div className="font-medium capitalize">{MONTHS[month]} {year}</div>
        <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="text-navy/40 hover:text-violet" data-testid="cal-next"><ChevronRight className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {DAYS.map((d, i) => <div key={i} className="text-[11px] text-navy/40 py-1">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const k = keyOf(d);
          const has = byDay[k];
          const isSel = selDay === k;
          return (
            <button key={i} onClick={() => setSelDay(isSel ? null : k)}
              className={`aspect-square rounded-lg text-sm flex flex-col items-center justify-center border transition-colors ${isSel ? "border-violet bg-violet/10" : has ? "border-violet/30 bg-violet/5" : "border-transparent hover:border-navy/10"}`}
              data-testid={has ? `cal-day-scheduled-${d.getDate()}` : `cal-day-${d.getDate()}`}>
              <span className={has ? "text-navy font-medium" : "text-navy/60"}>{d.getDate()}</span>
              {has && <span className="mt-0.5 text-[9px] bg-violet text-white rounded-full px-1.5 leading-tight">{has.length}</span>}
            </button>
          );
        })}
      </div>
      {selDay && (
        <div className="mt-4 border-t border-navy/5 pt-3 space-y-2" data-testid="cal-day-detail">
          {selPosts.length === 0 ? <p className="text-sm text-navy/40">Sin publicaciones programadas este día.</p> :
            selPosts.map((p) => (
              <div key={p.id} className="text-sm">
                <span className="text-violet font-medium">{new Date(p.scheduled_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span>
                <span className="text-navy/40 mx-1.5 capitalize">· {p.provider} · {p.target_label}</span>
                <div className="text-navy/70 truncate">{p.text}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
